/** Price book (§4.2): edit, add, retire, bulk-adjust. Changes never touch issued documents. */
import { useState } from 'preact/hooks';
import { createRecord, putRecord } from '../../core/db';
import { fmtCents, toCents } from '../../core/money';
import type { PriceItem, PriceKind } from '../../core/types';
import { pricesSig, refreshCore, toast } from '../state';
import { Banner } from '../bits';

const KIND_LABEL: Record<PriceKind, string> = { area: 'Area (sq ft)', line: 'Length (lin ft)', count: 'Count (each)', service: 'Service' };

export function PricesView() {
  const [showRetired, setShowRetired] = useState(false);
  const [bulkPct, setBulkPct] = useState('');
  const [nName, setNName] = useState(''); const [nUnit, setNUnit] = useState('each');
  const [nKind, setNKind] = useState<PriceKind>('service'); const [nRate, setNRate] = useState('');
  const prices = pricesSig.value.filter((p) => showRetired || !p.retired);

  async function setRate(p: PriceItem, dollars: number) {
    if (!Number.isFinite(dollars) || dollars < 0) return;
    await putRecord('priceBook', { ...p, rateCents: toCents(dollars) });
    await refreshCore();
  }

  async function toggleRetired(p: PriceItem) {
    await putRecord('priceBook', { ...p, retired: !p.retired });
    await refreshCore();
    toast(p.retired ? `${p.name} restored` : `${p.name} retired — hidden from pickers, history intact`);
  }

  async function bulkAdjust() {
    const pct = parseFloat(bulkPct);
    if (!Number.isFinite(pct) || pct === 0) { toast('Enter a percentage first, like 5 or -3'); return; }
    // Audit 2026-07-25: -150% turned every active rate negative in one click
    // ($1.10 → -$0.55). You cannot take more than all of a price away.
    if (pct <= -100) { toast('A cut of 100% or more is not a price. Use a smaller number.'); return; }
    for (const p of pricesSig.value.filter((x) => !x.retired)) {
      await putRecord('priceBook', { ...p, rateCents: Math.max(0, Math.round(p.rateCents * (1 + pct / 100))) });
    }
    await refreshCore();
    setBulkPct('');
    toast(`All active rates adjusted by ${pct}%. Issued documents are untouched.`);
  }

  async function addItem() {
    const rate = parseFloat(nRate);
    if (!nName.trim() || !Number.isFinite(rate)) { toast('Name and rate are required'); return; }
    // setRate() two functions up has always refused a negative rate; the create
    // path did not, so -$99 could be typed straight into the book and then bid.
    if (rate < 0) { toast('A rate cannot be negative. Use the Adjustment line on a bid to take money off.'); return; }
    const itemId = nName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 16) || `item${Date.now()}`;
    await createRecord('priceBook', {
      itemId, name: nName.trim(), unit: nUnit, kind: nKind, rateCents: toCents(rate), retired: false,
    });
    setNName(''); setNRate('');
    await refreshCore();
    toast(`${nName.trim()} added to the price book`);
  }

  return (
    <div>
      <div class="pagehead">
        <h1>Price book</h1>
        <label style="display:flex;align-items:center;gap:6px;font-weight:400;">
          <input type="checkbox" style="min-height:auto;" checked={showRetired}
            onChange={(e) => setShowRetired((e.target as HTMLInputElement).checked)} /> show retired
        </label>
      </div>

      <Banner kind="info">
        These rates are ballpark Phoenix numbers carried over from the prototype — review them before relying on a bid.
        Changing a rate never changes a bid that has already been issued.
      </Banner>

      <div class="card" style="margin-bottom:16px;display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end;">
        <div><label>Bulk adjust all active rates</label>
          <input aria-label="Bulk adjust percentage" type="number" inputMode="decimal" placeholder="%" style="width:100px;" value={bulkPct}
            onInput={(e) => setBulkPct((e.target as HTMLInputElement).value)} /></div>
        <button class="btn btn-ghost" onClick={() => void bulkAdjust()}>Apply percentage</button>
        <span style="flex:1;"></span>
        <div><label>New item</label>
          <input aria-label="New item name" placeholder="Name" value={nName} onInput={(e) => setNName((e.target as HTMLInputElement).value)} /></div>
        <div><label>Unit</label><input aria-label="New item unit" style="width:80px;" value={nUnit} onInput={(e) => setNUnit((e.target as HTMLInputElement).value)} /></div>
        <div><label>Kind</label>
          <select aria-label="New item kind" value={nKind} onChange={(e) => setNKind((e.target as HTMLSelectElement).value as PriceKind)}>
            {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
        <div><label>Rate $</label><input aria-label="New item rate dollars" type="number" inputMode="decimal" style="width:100px;" value={nRate}
          onInput={(e) => setNRate((e.target as HTMLInputElement).value)} /></div>
        <button class="btn btn-primary" onClick={() => void addItem()}>Add item</button>
      </div>

      <table class="data">
        <thead><tr><th>Item</th><th class="mono">id</th><th>Kind</th><th>Unit</th><th class="money">Rate</th><th></th></tr></thead>
        <tbody>
          {prices.map((p) => (
            <tr key={p.id} style={p.retired ? 'opacity:.55;' : ''}>
              <td style="font-weight:600;">{p.name}</td>
              <td class="mono small muted">{p.itemId}</td>
              <td class="muted">{KIND_LABEL[p.kind]}</td>
              <td class="muted">{p.unit}</td>
              <td class="money">
                <input type="number" inputMode="decimal" step="0.01" value={(p.rateCents / 100).toFixed(2)}
                  style="width:110px;text-align:right;min-height:32px;padding:3px 6px;" aria-label={`Rate for ${p.name}`}
                  onChange={(e) => void setRate(p, parseFloat((e.target as HTMLInputElement).value))} />
                <span class="muted small"> = {fmtCents(p.rateCents)}/{p.unit}</span>
              </td>
              <td><button class="btn btn-small btn-ghost" onClick={() => void toggleRetired(p)}>{p.retired ? 'Restore' : 'Retire'}</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
